import { uniq, without } from 'es-toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { assertUnlessInProduction } from '@paragon/client-tools';

import { SettingsPanels } from '../../components/interface/subpanels/settings/SettingsPanel';
import { SelectableItemEntry } from '../../library/selection/types';
import { startImportComponent } from './applicationSlice';
import { __UNSAFE_layoutItemsSelected } from './selectionsSlice';

type TransformMenuState =
  { readonly isOpen: true; readonly includesCoordinateOptions: boolean } | { readonly isOpen: false };

const settingsPanelDefaultSubPanel = 'loadSettings';

export type ReplaceMaterialsType = 'lumbers';
interface ReplaceMaterialsPanelData {
  readonly targetGuid: string;
  readonly replace: ReplaceMaterialsType;
}

// 'default' is closed on mobile or in a layout, open otherwise
type LeftSidebarType = 'closed' | 'open' | 'default' | 'negateDefault' | 'analysisHistory' | 'notifications';

export type TrussEditsPanelType =
  'stub' | 'gable' | 'reweb-zone' | 'reweb-profile' | 'span-macros' | 'overhang-macros' | 'heel-macros';

interface PanelData {
  readonly loadCasePanelOpen: boolean;
  readonly navigationPanelOpen: boolean;
  readonly leftSidebar: LeftSidebarType;
  readonly rightToolBarOpen: boolean;
  readonly preferredSidebar: 'left' | 'right'; // if both sidebars are open, only the preferred one will be visible on mobile
  readonly settingsPanelActiveModelGuid: string | null; // when set, the settings panel is open
  readonly replaceMaterialsPanel: ReplaceMaterialsPanelData | null; // when set, the replace materials panel is open
  readonly settingsPanelActiveSubPanel: SettingsPanels;
  readonly trussEditsPanel: TrussEditsPanelType | null;
  readonly matchFasciaHeightsPanelOpen: boolean;
  readonly matchFasciaHeightsPanelState: {
    readonly mode: 'heel-height' | 'overhang';
    readonly referencePlaneGuid: string | null;
  } | null;
  readonly optimizationPanelOpen: boolean;
  readonly transformMenuState: TransformMenuState;
  readonly checkResultExpandedItemIds: readonly string[];
  readonly expandedNavPanelRows: readonly string[]; // Note that this applies to both the explorer and the component panel.  Since they have different top-level objects and row ids are namespaced, expanding an item in one will never affect the other.
  readonly loadCaseExpandedItemIds: readonly string[];
  readonly loadCaseSortType: string;
  readonly loadCaseSortSelectedItemEntry: SelectableItemEntry | null;
  readonly renameLayoutComponentsPanel:
    | {
        readonly open: true;
        readonly componentDesignGuids: readonly string[];
        readonly sceneSelectedComponent: {
          readonly guid: string | null;
          readonly key: number; // can be bumped to trigger re-focusing the component if selected again
        };
      }
    | { readonly open: false };
}

const initialState: PanelData = {
  loadCasePanelOpen: false,
  navigationPanelOpen: true,
  leftSidebar: 'default',
  rightToolBarOpen: false,
  preferredSidebar: 'left',
  settingsPanelActiveModelGuid: null,
  replaceMaterialsPanel: null,
  settingsPanelActiveSubPanel: settingsPanelDefaultSubPanel,
  trussEditsPanel: null,
  matchFasciaHeightsPanelOpen: false,
  matchFasciaHeightsPanelState: null,
  optimizationPanelOpen: false,
  transformMenuState: { isOpen: false },
  checkResultExpandedItemIds: [],
  expandedNavPanelRows: ['componentDesignContainer'],
  loadCaseExpandedItemIds: [],
  loadCaseSortType: 'capacity',
  loadCaseSortSelectedItemEntry: null,
  renameLayoutComponentsPanel: { open: false },
};

const panelDataSlice = createSlice({
  name: 'panelData',
  initialState,
  reducers: {
    rightToolBarShowSet: (state, action: PayloadAction<boolean>) => {
      state.rightToolBarOpen = action.payload;
      state.preferredSidebar = 'right';
    },
    leftSidebarSet: (state, action: PayloadAction<LeftSidebarType>) => {
      state.leftSidebar = action.payload;
      state.preferredSidebar = 'left';
    },
    setSettingsPanelOpen: (
      state,
      action: PayloadAction<{ settingsPanelActiveModelGuid: string; panel: SettingsPanels }>,
    ) => {
      state.rightToolBarOpen = true;
      state.preferredSidebar = 'right';

      state.settingsPanelActiveModelGuid = action.payload.settingsPanelActiveModelGuid;
      state.settingsPanelActiveSubPanel = action.payload.panel;
    },
    setSettingsPanelClosed: (state) => {
      state.settingsPanelActiveModelGuid = null;
      state.settingsPanelActiveSubPanel = settingsPanelDefaultSubPanel;
      state.replaceMaterialsPanel = null;
    },
    setReplaceMaterialsPanelOpen: (state, action: PayloadAction<ReplaceMaterialsPanelData>) => {
      state.replaceMaterialsPanel = action.payload;
      state.preferredSidebar = 'right';
    },
    setReplaceMaterialsPanelClosed: (state) => {
      state.replaceMaterialsPanel = null;
    },
    setTrussEditsPanel: (state, action: PayloadAction<TrussEditsPanelType | null>) => {
      state.trussEditsPanel = action.payload;
      if (action.payload != null) {
        state.rightToolBarOpen = true;
        state.preferredSidebar = 'right';
      }
    },
    setMatchFasciaHeightsPanelOpen: (state) => {
      state.rightToolBarOpen = true;
      state.preferredSidebar = 'right';

      state.matchFasciaHeightsPanelOpen = true;
      state.matchFasciaHeightsPanelState = {
        mode: 'heel-height',
        referencePlaneGuid: null,
      };
    },
    setMatchFasciaHeightsPanelClosed: (state) => {
      state.matchFasciaHeightsPanelOpen = false;
      state.matchFasciaHeightsPanelState = null;
    },
    setOptimizationPanelOpen: (state) => {
      state.rightToolBarOpen = true;
      state.preferredSidebar = 'right';
      state.optimizationPanelOpen = true;
    },
    setOptimizationPanelClosed: (state) => {
      state.optimizationPanelOpen = false;
    },
    setFasciaHeightAdjustmentMode: (state, action: PayloadAction<'heel-height' | 'overhang'>) => {
      if (state.matchFasciaHeightsPanelState) {
        state.matchFasciaHeightsPanelState.mode = action.payload;
      } else {
        assertUnlessInProduction('fascia height adjustment mode set while panel is closed');
      }
    },
    setFasciaHeightReferencePlaneGuid: (state, action: PayloadAction<string | null>) => {
      if (state.matchFasciaHeightsPanelState) {
        state.matchFasciaHeightsPanelState.referencePlaneGuid = action.payload;
      } else {
        assertUnlessInProduction('fascia height adjustment reference plane set while panel is closed');
      }
    },
    openFullTransformMenu: (state) => {
      state.transformMenuState = { isOpen: true, includesCoordinateOptions: true };
    },
    openSnappingMenu: (state) => {
      state.transformMenuState = { isOpen: true, includesCoordinateOptions: false };
    },
    closeTransformMenu: (state) => {
      state.transformMenuState = { isOpen: false };
    },
    checkResultPanelExpandedItemIdsSelect: (state, action: PayloadAction<string[]>) => {
      state.checkResultExpandedItemIds = action.payload;
    },
    loadCasePanelExpandedItemIdsSelect: (state, action: PayloadAction<string[]>) => {
      state.loadCaseExpandedItemIds = action.payload;
    },
    loadCasePanelShowSet: (state, action: PayloadAction<boolean>) => {
      state.loadCasePanelOpen = action.payload;
    },
    componentPanelShowSet: (state, action: PayloadAction<boolean>) => {
      state.navigationPanelOpen = action.payload;
    },
    loadCasePanelSortType: (
      state,
      action: PayloadAction<{ sortType: string; selectedItemEntry: SelectableItemEntry | null }>,
    ) => {
      if (action.payload.sortType === 'csi' || action.payload.sortType === 'jsi') {
        state.loadCaseSortType = action.payload.sortType;
        state.loadCaseSortSelectedItemEntry = castDraft(action.payload.selectedItemEntry);
      } else {
        state.loadCaseSortType = action.payload.sortType;
      }
    },
    collapseNavigationPanelRow: (state, action: PayloadAction<string>) => {
      state.expandedNavPanelRows = without(state.expandedNavPanelRows, action.payload);
    },
    expandNavigationPanelRows: (state, action: PayloadAction<string[]>) => {
      state.expandedNavPanelRows = uniq(state.expandedNavPanelRows.concat(action.payload));
    },
    collapseAllExplorerPanelRows: (
      state,
      action: PayloadAction<{ componentDesignGuids: string[]; topLevelRowIdentifier: string }>,
    ) => {
      state.expandedNavPanelRows = state.expandedNavPanelRows.filter(
        (row) =>
          row === action.payload.topLevelRowIdentifier ||
          action.payload.componentDesignGuids.some((guid) => row.startsWith(guid)),
      );
    },
    collapseAllComponentPanelRows: (state, action: PayloadAction<{ componentDesignGuid: string }>) => {
      state.expandedNavPanelRows = state.expandedNavPanelRows.filter(
        (row) => row === action.payload.componentDesignGuid || !row.startsWith(action.payload.componentDesignGuid),
      );
    },

    openRenameLayoutComponentsPanel: (state, action: PayloadAction<{ componentDesignGuids: string[] }>) => {
      state.renameLayoutComponentsPanel = {
        open: true,
        sceneSelectedComponent: { guid: null, key: 1 },
        componentDesignGuids: action.payload.componentDesignGuids,
      };
    },

    addComponentsToRenamePanel: (state, action: PayloadAction<{ componentDesignGuids: string[] }>) => {
      if (state.renameLayoutComponentsPanel.open) {
        state.renameLayoutComponentsPanel.componentDesignGuids = uniq(
          state.renameLayoutComponentsPanel.componentDesignGuids.concat(action.payload.componentDesignGuids),
        );
      }
    },

    removeComponentsFromRenamePanel: (state, action: PayloadAction<{ componentDesignGuids: string[] }>) => {
      if (state.renameLayoutComponentsPanel.open) {
        state.renameLayoutComponentsPanel.componentDesignGuids = without(
          state.renameLayoutComponentsPanel.componentDesignGuids,
          ...action.payload.componentDesignGuids,
        );
      }
    },

    selectComponentInRenamePanel: (state, action: PayloadAction<string | null>) => {
      if (state.renameLayoutComponentsPanel.open) {
        state.renameLayoutComponentsPanel.sceneSelectedComponent.key += 1;
        state.renameLayoutComponentsPanel.sceneSelectedComponent.guid = action.payload;
      }
    },

    closeRenameLayoutComponentsPanel: (state) => {
      state.renameLayoutComponentsPanel = { open: false };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(__UNSAFE_layoutItemsSelected, (state, action) => {
      let buildingGuids: string[] = [];

      if (action.payload.type === 'building') {
        buildingGuids = action.payload.itemGuids;
      } else if (
        action.payload.hierarchyInfo &&
        action.payload.hierarchyInfo !== 'ghost' &&
        action.payload.hierarchyInfo.containingBuildingGuid != null
      ) {
        buildingGuids = [action.payload.hierarchyInfo.containingBuildingGuid];
      }

      // ensure the building(s) are expanded in the levels panel
      if (buildingGuids.length > 0) {
        state.expandedNavPanelRows = uniq(state.expandedNavPanelRows.concat(buildingGuids));
      }
    });

    builder.addCase(startImportComponent, (state) => {
      state.preferredSidebar = 'right';
    });
  },
});

export const {
  leftSidebarSet,
  rightToolBarShowSet,
  setSettingsPanelOpen,
  setSettingsPanelClosed,
  setReplaceMaterialsPanelOpen,
  setReplaceMaterialsPanelClosed,
  setTrussEditsPanel,
  setMatchFasciaHeightsPanelOpen,
  setMatchFasciaHeightsPanelClosed,
  setOptimizationPanelOpen,
  setOptimizationPanelClosed,
  setFasciaHeightAdjustmentMode,
  setFasciaHeightReferencePlaneGuid,
  openFullTransformMenu,
  openSnappingMenu,
  closeTransformMenu,
  checkResultPanelExpandedItemIdsSelect,
  loadCasePanelExpandedItemIdsSelect,
  loadCasePanelShowSet,
  componentPanelShowSet,
  loadCasePanelSortType,
  collapseNavigationPanelRow,
  expandNavigationPanelRows,
  collapseAllExplorerPanelRows,
  collapseAllComponentPanelRows,
  openRenameLayoutComponentsPanel,
  addComponentsToRenamePanel,
  removeComponentsFromRenamePanel,
  selectComponentInRenamePanel,
  closeRenameLayoutComponentsPanel,
} = panelDataSlice.actions;
export const panelData = panelDataSlice.reducer;
